import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { VENDOR_INDUSTRIES } from '../constants/industries'
import {
  Building2,
  MapPin,
  Globe,
  Linkedin,
  Star,
  Eye,
  Search,
  Filter,
  ArrowRight
} from 'lucide-react'

interface VendorProfile {
  id: string
  companyName: string
  tagline?: string
  description: string
  industry: string
  services: string[]
  location?: string
  website?: string
  linkedinUrl?: string
  phone?: string
  email?: string
  foundedYear?: string
  companySize?: string
  certifications: string[]
  languages: string[]
  keywords: string[]
  logo?: string
  coverImage?: string
  gallery: string[]
  isVerified: boolean
  isFeatured: boolean
  views: number
  likes: number
  contacts: number
  slug: string
  createdAt: string
  user: {
    id: string
    name: string
  }
  testimonials: Array<{
    id: string
    clientName: string
    clientCompany: string
    clientPosition?: string
    content: string
    rating: number
    isVerified: boolean
  }>
  portfolioItems: Array<{
    id: string
    title: string
    description: string
    category: string
    technologies: string[]
    images: string[]
    liveUrl?: string
    githubUrl?: string
    duration?: string
    budget?: number
  }>
}

interface VendorProfilesResponse {
  success: boolean
  data: {
    profiles: VendorProfile[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
  }
}

export default function VendorShowcasePage() {
  const [profiles, setProfiles] = useState<VendorProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIndustry, setSelectedIndustry] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalProfiles, setTotalProfiles] = useState(0)

  useEffect(() => {
    loadProfiles()
  }, [currentPage, searchTerm, selectedIndustry, selectedLocation])

  const loadProfiles = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12'
      })

      if (searchTerm) params.append('search', searchTerm)
      if (selectedIndustry) params.append('industry', selectedIndustry)
      if (selectedLocation) params.append('location', selectedLocation)

      const response = await fetch(`/api/vendor-profiles?${params}`)
      const data: VendorProfilesResponse = await response.json()

      if (data.success) {
        setProfiles(data.data.profiles)
        setTotalPages(data.data.pagination.pages)
        setTotalProfiles(data.data.pagination.total)
      }
    } catch (error) {
      console.error('Error loading profiles:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    loadProfiles()
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedIndustry('')
    setSelectedLocation('')
    setCurrentPage(1)
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ))
  }

  const getAverageRating = (testimonials: VendorProfile['testimonials']) => {
    if (testimonials.length === 0) return 0
    const sum = testimonials.reduce((acc, t) => acc + t.rating, 0)
    return Math.round((sum / testimonials.length) * 10) / 10
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vendor profiles...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Discover Amazing Vendors
            </h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
              Find verified vendors and service providers for your next project. 
              Browse portfolios, read reviews, and connect with the best talent.
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search vendors, services, or keywords..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white text-gray-900 border-0"
                  />
                </div>
                <Button type="submit" className="bg-white text-primary hover:bg-gray-100">
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-700">Filters:</span>
                </div>
                
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Industries</option>
                  {VENDOR_INDUSTRIES.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Locations</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Remote">Remote</option>
                </select>

                {(searchTerm || selectedIndustry || selectedLocation) && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {totalProfiles} Vendor{totalProfiles !== 1 ? 's' : ''} Found
          </h2>
          <p className="text-gray-600">
            {searchTerm && `Search results for "${searchTerm}"`}
            {selectedIndustry && ` in ${selectedIndustry}`}
            {selectedLocation && ` from ${selectedLocation}`}
          </p>
        </div>

        {/* Vendor Profiles Grid */}
        {profiles.length === 0 ? (
          <Card>
            <CardContent className="pt-12 pb-12 text-center">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No vendors found
              </h3>
              <p className="text-gray-600 mb-4">
                Try adjusting your search criteria or browse all vendors.
              </p>
              <Button onClick={clearFilters}>
                Browse All Vendors
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {profiles.map((profile) => (
                <Card key={profile.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        {profile.logo ? (
                          <img
                            src={profile.logo}
                            alt={profile.companyName}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-white" />
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-lg">{profile.companyName}</CardTitle>
                          {profile.tagline && (
                            <p className="text-sm text-gray-600">{profile.tagline}</p>
                          )}
                        </div>
                      </div>
                      {profile.isVerified && (
                        <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          Verified
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {profile.description}
                    </p>

                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      {profile.location && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {profile.location}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {profile.views} views
                      </div>
                    </div>

                    {profile.testimonials.length > 0 && (
                      <div className="flex items-center">
                        <div className="flex items-center mr-2">
                          {renderStars(getAverageRating(profile.testimonials))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {getAverageRating(profile.testimonials)} ({profile.testimonials.length} reviews)
                        </span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1">
                      {profile.services.slice(0, 3).map((service, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {service}
                        </span>
                      ))}
                      {profile.services.length > 3 && (
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{profile.services.length - 3} more
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex space-x-3">
                        {profile.website && (
                          <a
                            href={profile.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Globe className="w-4 h-4" />
                          </a>
                        )}
                        {profile.linkedinUrl && (
                          <a
                            href={profile.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <Linkedin className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <Button size="sm" className="bg-primary hover:bg-primary/90">
                        View Profile
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                
                <span className="px-4 py-2 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
